import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Drawer,
  Box,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  CircularProgress,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { useNavigate } from "react-router-dom";
import CampaignLeadCard from "./CampaignLeadCard";
import { Candidate } from "../../utils/CandidateDashboard";
import { LEAD_STATUS_OPTIONS } from "../../utils/campaignLeadHelpers";
import {
  fetchAllCampaignLeads,
  fetchCampaignLeadsPage,
} from "../../utils/fetchCampaignLeads";
import { exportLeadsToCsv } from "../../utils/exportLeadsToCsv";
import { roleBasedRedirection } from "../../utils/helper";
import { getCurrentSubscription } from "../../helper/subscriptionHelper";
import PaginationComponent from "../../utils/PaginationComponent";
import Toast from "../../utils/Toast";
import "../../styles/Listing/listing-premium.css";
import "../../styles/CampaignLeads/campaign-leads-drawer.css";

export interface CampaignLeadsDrawerCampaign {
  campaignId: string;
  name: string;
}

interface CampaignLeadsDrawerProps {
  open: boolean;
  campaign: CampaignLeadsDrawerCampaign | null;
  onClose: () => void;
}

const CampaignLeadsDrawer: React.FC<CampaignLeadsDrawerProps> = ({
  open,
  campaign,
  onClose,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [campaignTitle, setCampaignTitle] = useState("");
  const [filter, setFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const searchRef = useRef(searchTerm);

  const loadLeads = useCallback(
    async (
      pageIndex: number,
      limit: number,
      status: string,
      search: string
    ) => {
      if (!campaign?.campaignId) return;

      setLoading(true);
      const result = await fetchCampaignLeadsPage({
        campaignId: campaign.campaignId,
        page: pageIndex + 1,
        limit,
        status: status || undefined,
        search: search || undefined,
      });

      if (result) {
        setCandidates(result.candidates);
        setCount(result.count);
        setPage(result.page);
        setHasNextPage(result.hasNextPage);
        setHasPreviousPage(result.hasPreviousPage);
        setCampaignTitle(result.name || campaign.name);
      } else {
        setCandidates([]);
        setCount(0);
      }
      setLoading(false);
    },
    [campaign]
  );

  useEffect(() => {
    if (!open || !campaign?.campaignId) return;
    setFilter("");
    setSearchTerm("");
    searchRef.current = "";
    setPage(0);
    loadLeads(0, rowsPerPage, "", "");
  }, [open, campaign?.campaignId]);

  useEffect(() => {
    if (!open || !campaign?.campaignId) return;
    loadLeads(page, rowsPerPage, filter, searchRef.current);
  }, [page, rowsPerPage]);

  const handleFilterChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setFilter(value);
    setPage(0);
    loadLeads(0, rowsPerPage, value, searchRef.current);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    searchRef.current = value;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(0);
      loadLeads(0, rowsPerPage, filter, value);
    }, 350);
  };

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const viewProfile = (id: number) =>
    navigate(roleBasedRedirection("/candidate-leads-dashboard/view"), {
      state: { id, filter, campaignID: campaign?.campaignId },
    });

  const handleCallToAction = async (id: number) => {
    try {
      const { isActive, subscription } = await getCurrentSubscription();
      if (!isActive) {
        navigate("/subscriptions");
        return;
      }
      if (subscription?.type?.toLowerCase()?.trim() === "basic") {
        navigate("/update-subscriptions");
        return;
      }
      viewProfile(id);
    } catch {
      navigate("/subscriptions");
    }
  };

  const handleExportCsv = async () => {
    if (!campaign?.campaignId) return;

    setExporting(true);
    try {
      const allLeads = await fetchAllCampaignLeads(
        campaign.campaignId,
        filter || undefined,
        searchRef.current || undefined
      );

      if (!allLeads.length) {
        Toast("No leads to export for this campaign.");
        return;
      }

      exportLeadsToCsv(allLeads, campaignTitle || campaign.name);
      Toast(`Exported ${allLeads.length} lead(s) to CSV.`, "success");
    } catch {
      Toast("Failed to export leads. Please try again.", "error");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      className="campaign-leads-drawer"
      ModalProps={{ keepMounted: false }}
      PaperProps={{ className: "campaign-leads-drawer-paper" }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <div className="campaign-leads-drawer-header">
          <div className="campaign-leads-drawer-header-main">
            <div className="campaign-leads-drawer-header-text">
              <div className="campaign-leads-drawer-title-row">
                <span className="campaign-leads-drawer-badge">
                  <PeopleOutlineIcon sx={{ fontSize: 13 }} />
                  Campaign Leads
                </span>
                <h2 className="campaign-leads-drawer-title">
                  {campaignTitle || campaign?.name || "Campaign"}
                </h2>
              </div>
              <p
                className="campaign-leads-drawer-subtitle"
                title={campaign?.campaignId}
              >
                {campaign?.campaignId
                  ? `${campaign.campaignId.slice(0, 18)}…`
                  : "—"}
              </p>
            </div>

            <div className="campaign-leads-drawer-header-actions">
              <span className="campaign-leads-drawer-count-pill">
                <strong>{count}</strong> leads
              </span>
              <button
                type="button"
                className="campaign-leads-drawer-export-btn"
                onClick={handleExportCsv}
                disabled={exporting || loading}
              >
                {exporting ? (
                  <CircularProgress size={14} sx={{ color: "#1e3a52" }} />
                ) : (
                  <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />
                )}
                Export
              </button>
              <button
                type="button"
                className="campaign-leads-drawer-close"
                onClick={onClose}
                aria-label="Close drawer"
              >
                <CloseRoundedIcon sx={{ fontSize: 18 }} />
              </button>
            </div>
          </div>
        </div>

        <div className="campaign-leads-drawer-toolbar">
          <div className="campaign-leads-drawer-search-wrap">
            <span className="campaign-leads-drawer-field-label">Search</span>
            <div className="campaign-leads-drawer-search">
              <SearchIcon sx={{ color: "#94a3b8", fontSize: 21 }} />
              <input
                type="text"
                placeholder="Name, email, phone, job title..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
          </div>
          <FormControl size="small" className="campaign-leads-drawer-filter">
            <span className="campaign-leads-drawer-field-label">Status</span>
            <Select
              value={filter}
              onChange={handleFilterChange}
              displayEmpty
              renderValue={(selected) =>
                LEAD_STATUS_OPTIONS.find((o) => o.value === selected)?.label ||
                "All Candidates"
              }
            >
              {LEAD_STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value || "all"} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        <div className="campaign-leads-drawer-body">
          {loading ? (
            <div className="campaign-leads-drawer-loading">
              <CircularProgress size={40} sx={{ color: "#56c6a9" }} />
              <span>Loading candidate leads...</span>
            </div>
          ) : candidates.length > 0 ? (
            <>
              <div className="campaign-leads-drawer-results-label">
                Showing {candidates.length} of {count} leads
              </div>
              <div className="campaign-leads-drawer-list">
                {candidates.map((lead) => (
                  <CampaignLeadCard
                    key={lead.id}
                    lead={lead}
                    inDrawer
                    onCallToAction={handleCallToAction}
                  />
                ))}
              </div>
            </>
          ) : (
            <div className="campaign-leads-drawer-empty">
              <div className="campaign-leads-drawer-empty-icon">
                <PersonOutlineIcon />
              </div>
              <h3>No Leads Found</h3>
              <p>
                {filter || searchTerm
                  ? "No leads match your current filters. Try adjusting search or status."
                  : "No candidate leads have been added to this campaign yet."}
              </p>
            </div>
          )}
        </div>

        {!loading && count > 0 && (
          <div className="campaign-leads-drawer-pagination campaign-leads-drawer-pagination--compact">
            <PaginationComponent
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              count={count}
              hasNextPage={hasNextPage}
              hasPreviousPage={hasPreviousPage}
            />
          </div>
        )}
      </Box>
    </Drawer>
  );
};

export default CampaignLeadsDrawer;
